// Client for the live NHC proxy (backend/main.py). In dev, requests hit the
// Vite proxy (/api -> localhost:8000). In prod, set VITE_API_BASE to the
// deployed API origin.

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

/** Absolute URL for a backend path (server-rendered map images/tiles are
 *  loaded by MapLibre, which needs the API origin in production). */
export const apiUrl = (path: string) => `${API_BASE}${path}`;

export interface LiveTrackPoint {
  dt: string | null;
  lat: number;
  lon: number;
  vmax: number;
  pmin: number;
  r34?: number[] | null;
  r50?: number[] | null;
  r64?: number[] | null;
  vf?: number | null; // forward speed (kt), parser-derived
  delta_v_24hr?: number | null; // 24h intensity change (kt) — RI signal at >=+30
  delta_p_24hr?: number | null; // 24h pressure change (mb) — negative = deepening
  storm_type?: string | null; // HU/TS/TD/EX… (B-deck classification)
}

export interface ForecastPoint {
  tau: number;
  lat: number;
  lon: number;
  vmax: number;
  pmin: number;
  storm_type?: string;
}

export interface ModelRun {
  tech: string;
  tech_name: string;
  color: string;
  init_time: string | null;
  age_h?: number | null; // hours older than the newest init in the bundle
  points: ForecastPoint[];
  derived?: string; // drawn from ensemble members because NHC's own line is cut short (GDMN)
}

// Statistical-dynamical / consensus intensity aids — the NHC intensity suite.
// tau -> vmax only: their A-deck positions are the official track copied
// along, so they never enter the track spaghetti or spread.
export interface IntensityRun {
  tech: string;
  tech_name: string;
  init_time: string | null;
  age_h?: number | null;
  points: { tau: number; vmax: number }[];
}

export interface EnsembleMember {
  tech: string; // AC00/AP01-AP30 (GEFS), ENS01.., AIFS01..
  points: { tau: number; lat: number; lon: number; vmax: number; pmin?: number }[];
}

export interface StormEnsemble {
  init_time: string;
  n_members: number;
  members: EnsembleMember[];
}

// Multi-source suite: GEFS (NOAA physics), ENS (ECMWF physics), AIFS (ECMWF AI),
// FNV3 (Google DeepMind's cyclone model via Weather Lab — the 1000-member
// large ensemble, 50-member fallback)
export interface EnsembleSet extends StormEnsemble {
  source: string; // "GEFS" | "ENS" | "AIFS" | "FNV3"
  kind: "physics" | "ai";
  label: string;
  match_nm?: number | null; // ECMWF sets: distance to the matched cluster (cross-invest guard)
  // A system the feed doesn't name yet (a new invest): members picked by
  // where their own low forms; ens_size is the whole ensemble, members that
  // never form it included
  matched_by?: "formation";
  ens_size?: number;
  model?: string; // DeepMind: which Weather Lab model produced the set
  attribution?: string | null; // DeepMind: © notice that must travel with anything shared
  terms_url?: string | null;
}

// Ensemble sources: one toggle chip + one member colour each. The sidebar
// chips and the map's member traces both read this table.
export const ENSEMBLE_SOURCES: { source: string; name: string; color: string; kind: "physics" | "ai" }[] = [
  { source: "GEFS", name: "GEFS", color: "#94A3B8", kind: "physics" },
  { source: "ENS", name: "ECMWF ENS", color: "#34D399", kind: "physics" },
  { source: "AIFS", name: "AIFS", color: "#FB7185", kind: "ai" },
  { source: "FNV3", name: "DeepMind", color: "#E879F9", kind: "ai" },
];
export const ensembleColor = (source: string) =>
  ENSEMBLE_SOURCES.find((s) => s.source === source)?.color ?? "#94A3B8";
export const ensembleName = (source: string) =>
  ENSEMBLE_SOURCES.find((s) => s.source === source)?.name ?? source;

// Live Threat Fingerprint + footprint params (same shapes as replay frames —
// the SPA renders live footprints through the identical geometry code)
export interface LiveThreat {
  idx: Record<string, number>; // CDI/IKE/FSTI/IFI/SGM, 0-10
  labels: Record<string, string>;
  overall: [number, string, string]; // score, label, color
  quality?: unknown;
  foot: {
    mb: number | null;
    torn?: { tier: number; r34r: number };
    flood?: { tier: number; radii: number[] };
    surge?: [number, number][];
  };
}

export interface StormBundle {
  storm_id: string;
  name: string;
  latest_init?: string | null; // newest guidance init = the advisory-cycle id
  official_init?: string | null; // OFCL init — forecast valid time = official_init + tau
  threat?: LiveThreat | null; // Explorer-parity fingerprint for the live storm
  // aids excluded for being older than the freshness gate (surfaced, not hidden)
  stale_aids?: { tech: string; tech_name: string; init_time: string | null; age_h: number }[];
  // upstream ensemble feed status per source (ok | no_match | stale |
  // unavailable | disabled) — a missing ensemble should be explained, not mysterious
  ensemble_feeds?: Record<string, { cycle: string | null; status: string }>;
  // intensity-only aids (SHIPS/LGEM/HCCA): tau -> vmax, never on the map
  intensity_guidance?: Record<string, IntensityRun>;
  best_track: LiveTrackPoint[];
  official_forecast: ForecastPoint[];
  model_runs: Record<string, ModelRun>;
  ensemble?: StormEnsemble | null; // legacy GEFS slot (logged bundles predate ensembles[])
  ensembles?: EnsembleSet[];
  // lead-time-normalized: ratio = spread_nm / typical track error at that tau
  spread: Record<string, { n_models: number; spread_nm: number; typical_nm: number; ratio: number }>;
  source: string;
  as_of?: string | null; // demo snapshot init time (pre-impact forecast moment)
  updated: string;
  stale?: boolean; // served from cache because NHC was unreachable
  guidance_unavailable?: boolean; // B-deck only (young invest, no model runs yet)
}

export interface StormRef {
  storm_id: string;
  name?: string | null;
  year?: number;
  number?: number;
  label?: string;
  basin?: string; // AL / EP / CP
  is_invest?: boolean;
  deck_updated?: string | null; // last A-deck file update at NHC
  last_fix?: string | null;
  latest?: LatestFix | null; // active systems only: newest best-track fix (map icons)
  formerly?: string[]; // invests NHC designated as this storm (their old links follow it)
}

/** A system's newest working-best-track fix, from /api/active. */
export interface LatestFix {
  dt: string; // naive UTC
  lat: number;
  lon: number;
  vmax: number | null; // kt
  mslp: number | null; // mb
  type: string | null; // ATCF TY: HU / TS / TD / SS / SD / EX / LO / DB / WV …
  dv24: number | null; // wind change over 24 h, kt
  dp24: number | null; // pressure change over 24 h, mb
  move_deg: number | null; // from the fixes 12 h apart
  move_kt: number | null;
  // has any fix been a tropical cyclone? false on a numbered system = a
  // Potential Tropical Cyclone (NHC advises before it forms)
  ever_tc?: boolean;
}

// Selectable spaghetti models (OFCL is always shown as the official forecast).
// Keep this list and the backend's DEFAULT_MODELS (config.py) the same set: the
// server keeps that bundle warm. UKMET (UKX) and the Canadian (CMC) run twice a day.
// HAFS-A/B are NOAA's operational hurricane models; HWRF/HMON were retired
// after 2024 but stay listed so archived demo storms still render. GDMN is
// Google DeepMind's cyclone-model ensemble mean as NHC carries it in the A-deck.
export const SPAGHETTI_MODELS: { tech: string; name: string; color: string }[] = [
  { tech: "AVNO", name: "GFS", color: "#60A5FA" },
  { tech: "EMXI", name: "ECMWF", color: "#EF4444" },
  { tech: "UKX", name: "UKMET", color: "#22D3EE" },
  { tech: "CMC", name: "CMC", color: "#A3E635" },
  { tech: "GDMN", name: "DeepMind", color: "#E879F9" },
  { tech: "HFSA", name: "HAFS-A", color: "#FB7185" },
  { tech: "HFSB", name: "HAFS-B", color: "#FDBA74" },
  { tech: "HWRF", name: "HWRF", color: "#22C55E" },
  { tech: "HMON", name: "HMON", color: "#A855F7" },
  { tech: "TVCN", name: "Consensus", color: "#FBBF24" },
];

// The API answers 502/503/504 for the few seconds a restart or deploy takes
// (Render serves its own 502 page meanwhile). Ride that out instead of
// showing an error: retry with backoff, ~30 s in all. Other failures
// (404 etc.) are real answers and surface at once.
const RETRY_DELAYS_MS = [2000, 5000, 9000, 14000];
const RETRYABLE = new Set([502, 503, 504]);

async function getJson<T>(path: string): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    let res: Response | null = null;
    try {
      res = await fetch(`${API_BASE}${path}`);
    } catch (e) {
      // network error (connection reset mid-restart); rethrow once out of retries
      if (attempt >= RETRY_DELAYS_MS.length) throw e;
    }
    if (res?.ok) return res.json();
    if (res && (!RETRYABLE.has(res.status) || attempt >= RETRY_DELAYS_MS.length)) {
      throw new Error(`${path} → HTTP ${res.status}`);
    }
    await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
  }
}

export function fetchActive() {
  return getJson<{ storms: StormRef[]; updated: string; feed_ok?: boolean }>(
    "/api/active",
  );
}

export function fetchDemo() {
  return getJson<{ storms: StormRef[] }>("/api/demo");
}

export function fetchStorm(stormId: string, models: string[]) {
  const q = models.length ? `?models=OFCL,${models.join(",")}` : "";
  return getJson<StormBundle>(`/api/storm/${stormId}${q}`);
}

// ── Advisory log: run-to-run history (backend/advisory_log.py) ──
// One full bundle is persisted per guidance cycle; a logged cycle is byte-
// compatible with the live /api/storm bundle, so the scrubber just fetches an
// old cycle and renders it through the existing live path.

// Per-cycle summary (drives the scrubber list + delta readout — no bundle fetch).
export interface CycleSummary {
  cycle: string; // ISO init time = the cycle id
  logged_at?: string;
  lat: number | null;
  lon: number | null;
  vmax: number | null;
  pmin: number | null;
  n_aids: number;
  n_members: number;
  spread48_nm: number | null;
  spread48_ratio: number | null;
}

export interface LogStormRef {
  storm_id: string;
  n_cycles: number;
  first_cycle: string | null;
  last_cycle: string | null;
}

// Storms with at least one logged cycle (newest activity first).
export function fetchLogStorms() {
  return getJson<{ storms: LogStormRef[] }>("/api/log");
}

// Cycle index for one storm (oldest→newest). 404 → no logged cycles.
export function fetchLogIndex(stormId: string) {
  return getJson<{ storm_id: string; cycles: CycleSummary[] }>(
    `/api/log/${stormId}`,
  );
}

// One logged cycle as a full bundle (same shape as fetchStorm). The backend
// normalizes the cycle id, so the raw ISO string from the index works.
export function fetchLogCycle(stormId: string, cycle: string) {
  return getJson<StormBundle>(
    `/api/log/${stormId}/${encodeURIComponent(cycle)}`,
  );
}

// ── Genesis Watch: ECMWF pre-genesis disturbances (AL/EP/CP) ──
export interface GenesisSource {
  source: string; // ENS (physics) | AIFS (AI)
  id: string; // ECMWF disturbance id, e.g. "70L"
  n: number;
  total: number;
  pct: number; // tracking/support % (members tracking a coherent low)
  develop_pct: number; // genesis proxy: members reaching >=34 kt / total
  peak_vmax: number; // forecast-window peak any member reaches (15-day)
  n_develop: number; // members reaching >=34 kt somewhere in the window
}
export interface NhcOutlookRef {
  prob: number | null; // NHC 7-day genesis %
  risk: string | null; // low | medium | high
  prob48: number | null;
  title: string;
}
export interface GenesisItem {
  lat: number;
  lon: number;
  basins: string[];
  both: boolean; // tracked by BOTH physics + AI ensembles
  max_support_pct: number;
  max_develop_pct: number; // ranks/colors the list (genesis signal)
  peak_vmax: number;
  heading: string | null;
  heading_deg: number | null;
  sources: GenesisSource[];
  nhc7?: NhcOutlookRef; // nearest located NHC genesis area (AL/EP only)
}
export interface NhcArea {
  n: number;
  basin: string;
  title: string;
  prob48: number | null;
  risk48: string | null;
  prob7: number | null;
  risk7: string | null;
  invest: string | null;
  lat: number | null;
  lon: number | null;
}
// NHC GTWO geometry — the official colour-coded genesis areas + ✕ markers
export interface GtwoFeature {
  kind: "area" | "point";
  basin: string;
  prob7: number | null;
  risk7: string | null;
  prob2: number | null;
  risk2?: string | null;
  ring?: [number, number][]; // area polygon
  lat?: number; // point
  lon?: number;
  id?: string; // "epa1": basin + "a" + NHC's outlook number (a ✕ carries its area's) — a futurecast subject
  n?: number | null; // NHC's outlook number
  title?: string; // the place NHC names ("South of Southern Mexico")
  discussion?: string | null; // NHC's prose, without the formation-chance lines
  invest?: string | null; // the designation NHC's text gives it ("EP92")
  invest_id?: string | null; // the active invest NHC has made of the area ("ep922026")
}
export interface GenesisWatch {
  cycle: string | null;
  watch: GenesisItem[];
  nhc?: NhcArea[]; // NHC official 2/7-day outlook text (Atlantic + E Pacific)
  gtwo?: GtwoFeature[]; // NHC outlook geometry for the map
}

export function fetchGenesis() {
  return getJson<GenesisWatch>("/api/genesis");
}

// ── OFFICIAL NHC products (forecast cone + coastal watches/warnings) ──
export interface OfficialGis {
  cone: { ring: [number, number][] } | null; // [lon, lat]
  ww: { path: [number, number][]; code: string | null; color: string; label: string }[];
  advisory: { num: string | null; date: string | null; storm: string | null; type: string | null } | null;
}

export function fetchStormGis(stormId: string) {
  return getJson<OfficialGis>(`/api/nhc_gis/${stormId}`);
}

// ── NHC public-advisory ledger (backend data/nhc_advisories.py) ──
// Every public advisory — full AND intermediate ("12A") — with the official
// wind, central pressure, position and motion at its valid time: the
// update-to-update record. Empty for invests (no advisories yet).
export interface Advisory {
  adv: string; // "12A"
  num: number;
  suffix: string; // "" | "A" | "B"
  kind: "full" | "intermediate" | "special";
  issued: string; // ISO UTC — the advisory's valid time ("SUMMARY OF ... 1800 UTC")
  cls: string | null; // HU / TS / TD / PTC / PT / SS / SD / RM
  name: string | null;
  lat: number | null;
  lon: number | null;
  vmax: number | null; // kt (recovered exactly from the bulletin's mph)
  mph: number | null;
  pmin: number | null; // mb
  move_dir: string | null; // "NW" | "STNRY"
  move_deg: number | null;
  move_kt: number | null;
  move_mph: number | null;
  url?: string;
}

export function fetchAdvisories(stormId: string) {
  return getJson<{ storm_id: string; advisories: Advisory[] }>(`/api/advisories/${stormId}`);
}

// ── Hurricane-hunter reconnaissance (backend data/nhc_recon.py) ──
// Vortex-message center fixes: the aircraft-MEASURED central pressure.
export interface ReconFix {
  t: string; // ISO UTC fix time
  lat: number;
  lon: number;
  mslp: number | null; // mb
  extrap: boolean; // extrapolated from flight level (vs dropsonde)
  eye: string | null; // "CLOSED C12" / "OPEN SE CO10-20"
  eye_nm: number | null;
  max_fl_kt: number | null; // max flight-level wind
  max_sfc_kt: number | null; // max SFMR surface wind estimate
  aircraft: string | null; // AF305 / NOAA3
  mission: string | null;
  ob: number | null;
}
// 30-s flight observations (HDOB)
export interface ReconOb {
  t: string;
  lat: number;
  lon: number;
  fl_dir: number | null;
  fl_kt: number | null; // flight-level wind
  sfmr_kt: number | null; // SFMR surface wind
  slp: number | null; // extrapolated surface pressure (low flight levels only)
  t_c: number | null; // flight-level temperature
  rain_mmhr: number | null;
}
export interface ReconFlight {
  aircraft: string;
  mission: string;
  start: string | null;
  end: string | null;
  obs: ReconOb[];
}
export interface ReconData {
  fixes: ReconFix[];
  flights: ReconFlight[];
  since: string;
  until: string;
}

// NHC Potential Storm Surge Flooding map: which advisory's map applies + tiles
export function fetchSurge(stormId: string, asOf: string | null) {
  const q = asOf ? `?as_of=${encodeURIComponent(asOf)}` : "";
  return getJson<{
    available: boolean; adv?: string; current_adv?: number | null;
    bounds?: [number, number, number, number]; tiles?: string;
  }>(`/api/surge/${stormId}${q}`);
}

export function fetchRecon(stormId: string, opts: { until?: string | null; name?: string | null } = {}) {
  const q = new URLSearchParams();
  if (opts.until) q.set("until", opts.until);
  if (opts.name) q.set("name", opts.name);
  const qs = q.toString();
  return getJson<ReconData>(`/api/recon/${stormId}${qs ? `?${qs}` : ""}`);
}

// ── NWS active alerts (api.weather.gov — keyless, CORS-open, straight from
// the browser). Warnings carry polygons; zone-based alerts without geometry
// are skipped (rendering zones would need the whole zone DB).
export interface NwsAlert {
  id: string;
  event: string;
  headline: string | null;
  polygon: [number, number][]; // [lon, lat]
}

const ALERT_EVENTS = [
  "Tornado Warning", "Flash Flood Warning", "Hurricane Warning",
  "Tropical Storm Warning", "Storm Surge Warning", "Extreme Wind Warning",
];

export async function fetchNwsAlerts(): Promise<NwsAlert[]> {
  try {
    const url = `https://api.weather.gov/alerts/active?event=${encodeURIComponent(ALERT_EVENTS.join(","))}`;
    const r = await fetch(url, { headers: { Accept: "application/geo+json" } });
    if (!r.ok) return [];
    const d = (await r.json()) as {
      features?: {
        id: string;
        geometry: { type: string; coordinates: number[][][] } | null;
        properties: { event: string; headline?: string };
      }[];
    };
    const out: NwsAlert[] = [];
    for (const f of d.features ?? []) {
      if (!f.geometry || f.geometry.type !== "Polygon") continue;
      out.push({
        id: f.id,
        event: f.properties.event,
        headline: f.properties.headline ?? null,
        polygon: f.geometry.coordinates[0] as [number, number][],
      });
    }
    return out;
  } catch {
    return [];
  }
}
